import React from 'react';
import {
  OutputTemplate,
  TemplateColumn,
  TemplateFilter,
  TemplateReportType,
  TemplateFileFormat,
  FilterCondition,
  NamingElement,
  NamingSeparator,
} from '../../../types/packager-types';
import useGetAvailableColumns, {
  AvailableColumn,
} from '../../hooks/packager/useGetAvailableColumns';
import { generateNamingPreview, validateTemplate } from './templateEditorUtils';

export interface TemplateEditorFormState {
  templateName: string;
  reportType: TemplateReportType;
  columns: TemplateColumn[];
  filters: TemplateFilter[];
  fileFormat: TemplateFileFormat;
  splitBy: string;
  defaultSplitValue: string;
  namingElements: NamingElement[];
  namingSeparator: NamingSeparator;
  namingMaxLength: number;
  nameError: string;
  columnsError: string;
  namingError: string;
  isDirty: boolean;
}

export interface UseTemplateEditorFormProps {
  open: boolean;
  editingTemplate: { name: string; template: OutputTemplate } | null;
  existingTemplateNames: string[];
  dataPoolId: string;
  packagerId: string;
  onSave: (name: string, template: OutputTemplate) => void;
  onClose: () => void;
}

export interface UseTemplateEditorFormReturn {
  // State
  state: TemplateEditorFormState;
  availableFields: AvailableColumn[];
  stringFields: AvailableColumn[];
  isLoadingColumns: boolean;

  // Setters
  setTemplateName: (name: string) => void;
  setNameError: (error: string) => void;
  setColumnsError: (error: string) => void;
  setNamingError: (error: string) => void;

  // Split-by fields (uses supports_split_by from API when available)
  splitByFields: AvailableColumn[];

  // Handlers
  handleReportTypeChange: (newType: TemplateReportType) => void;
  handleFileFormatChange: (format: TemplateFileFormat) => void;
  handleSplitByChange: (value: string) => void;
  handleDefaultSplitValueChange: (value: string) => void;
  handleNamingSeparatorChange: (separator: NamingSeparator) => void;
  handleNamingMaxLengthChange: (length: number) => void;

  // Column management
  addColumn: () => void;
  updateColumn: (index: number, updates: Partial<TemplateColumn>) => void;
  removeColumn: (index: number) => void;
  moveColumn: (fromIndex: number, toIndex: number) => void;

  // Filter management
  addFilter: () => void;
  updateFilter: (index: number, updates: Partial<TemplateFilter>) => void;
  updateFilterValue: (
    index: number,
    value: string | string[] | [string, string],
    subIndex?: number
  ) => void;
  removeFilter: (index: number) => void;

  // Naming element management
  addNamingElement: () => void;
  updateNamingElement: (index: number, updates: Partial<NamingElement>) => void;
  removeNamingElement: (index: number) => void;
  moveNamingElement: (fromIndex: number, toIndex: number) => void;

  // Form actions
  handleSave: () => void;
  getNamingPreview: () => string;
}

const useTemplateEditorForm = ({
  open,
  editingTemplate,
  existingTemplateNames,
  dataPoolId,
  packagerId,
  onSave,
  onClose,
}: UseTemplateEditorFormProps): UseTemplateEditorFormReturn => {
  const [templateName, setTemplateName] = React.useState('');
  const [reportType, setReportType] = React.useState<TemplateReportType>(
    TemplateReportType.ACTIVITY
  );
  const [columns, setColumns] = React.useState<TemplateColumn[]>([]);
  const [filters, setFilters] = React.useState<TemplateFilter[]>([]);
  const [fileFormat, setFileFormat] = React.useState<TemplateFileFormat>(TemplateFileFormat.CSV);
  const [splitBy, setSplitBy] = React.useState<string>('');
  const [defaultSplitValue, setDefaultSplitValue] = React.useState('unknown');
  const [namingElements, setNamingElements] = React.useState<NamingElement[]>([]);
  const [namingSeparator, setNamingSeparator] = React.useState<NamingSeparator>('_');
  const [namingMaxLength, setNamingMaxLength] = React.useState(100);
  const [nameError, setNameError] = React.useState('');
  const [columnsError, setColumnsError] = React.useState('');
  const [namingError, setNamingError] = React.useState('');
  const [isDirty, setIsDirty] = React.useState(false);

  // Fetch available columns from API based on selected report type
  const { columns: availableColumns, isLoading: isLoadingColumns } = useGetAvailableColumns(
    dataPoolId,
    packagerId,
    reportType,
    open && !!dataPoolId && !!packagerId
  );

  const availableFields: AvailableColumn[] = Array.isArray(availableColumns) ? availableColumns : [];

  // For split-by: use supports_split_by from API when available, otherwise fall back to string type
  const splitByFields = availableFields.filter((f) => {
    // If API provides supports_split_by, use it
    if (f.supports_split_by !== undefined) {
      return f.supports_split_by === true;
    }
    // Fallback: allow string types for split-by
    return f.type === 'string';
  });

  // Keep stringFields for backwards compatibility
  const stringFields = availableFields.filter((f) => f.type === 'string');

  // Reset form when dialog opens/closes or editing template changes
  React.useEffect(() => {
    if (open) {
      if (editingTemplate) {
        setTemplateName(editingTemplate.name);
        setReportType(editingTemplate.template.report_type || TemplateReportType.ACTIVITY);
        // Ensure all columns have ids for stable DnD
        const columnsWithIds = (editingTemplate.template.columns || []).map(col =>
          col.id ? col : { ...col, id: crypto.randomUUID() }
        );
        setColumns(columnsWithIds);
        setFilters(editingTemplate.template.filters || []);
        setFileFormat(editingTemplate.template.file_format || TemplateFileFormat.CSV);
        setSplitBy(editingTemplate.template.split_by || '');
        setDefaultSplitValue(editingTemplate.template.default_split_value || 'unknown');
        setNamingElements(editingTemplate.template.naming_config?.elements || []);
        setNamingSeparator(editingTemplate.template.naming_config?.separator || '_');
        setNamingMaxLength(editingTemplate.template.naming_config?.max_length || 100);
      } else {
        setTemplateName('');
        setReportType(TemplateReportType.ACTIVITY);
        setColumns([]);
        setFilters([]);
        setFileFormat(TemplateFileFormat.CSV);
        setSplitBy('');
        setDefaultSplitValue('unknown');
        setNamingElements([]);
        setNamingSeparator('_');
        setNamingMaxLength(100);
      }
      setNameError('');
      setColumnsError('');
      setNamingError('');
      setIsDirty(false);
    }
  }, [open, editingTemplate]);

  // Report type change handler
  const handleReportTypeChange = (newType: TemplateReportType) => {
    setReportType(newType);
    setColumns([]);
    setFilters([]);
    setSplitBy('');
    setIsDirty(true);
  };

  // Simple setters with dirty tracking
  const handleFileFormatChange = (format: TemplateFileFormat) => {
    setFileFormat(format);
    setIsDirty(true);
  };

  const handleSplitByChange = (value: string) => {
    setSplitBy(value);
    setIsDirty(true);
  };

  const handleDefaultSplitValueChange = (value: string) => {
    setDefaultSplitValue(value);
    setIsDirty(true);
  };

  const handleNamingSeparatorChange = (separator: NamingSeparator) => {
    setNamingSeparator(separator);
    setIsDirty(true);
  };

  const handleNamingMaxLengthChange = (length: number) => {
    setNamingMaxLength(length);
    setIsDirty(true);
  };

  // Column management
  const addColumn = () => {
    if (availableFields.length === 0) return;
    const firstField = availableFields[0];
    setColumns([
      ...columns,
      {
        id: crypto.randomUUID(),
        field: firstField.name,
        header: firstField.name,
        order: columns.length
      },
    ]);
    setColumnsError('');
    setIsDirty(true);
  };

  const updateColumn = (index: number, updates: Partial<TemplateColumn>) => {
    const newColumns = [...columns];
    newColumns[index] = { ...newColumns[index], ...updates };
    setColumns(newColumns);
    setColumnsError('');
    setIsDirty(true);
  };

  const removeColumn = (index: number) => {
    const newColumns = columns.filter((_, i) => i !== index);
    setColumns(newColumns.map((c, i) => ({ ...c, order: i })));
    setColumnsError('');
    setIsDirty(true);
  };

  const moveColumn = (fromIndex: number, toIndex: number) => {
    const newColumns = [...columns];
    const [removed] = newColumns.splice(fromIndex, 1);
    newColumns.splice(toIndex, 0, removed);
    setColumns(newColumns.map((c, i) => ({ ...c, order: i })));
    setIsDirty(true);
  };

  // Filter management
  const addFilter = () => {
    if (availableFields.length === 0) return;
    const firstField = availableFields[0];
    setFilters([
      ...filters,
      { field: firstField.name, condition: FilterCondition.EQUALS, value: '' },
    ]);
    setIsDirty(true);
  };

  const updateFilter = (index: number, updates: Partial<TemplateFilter>) => {
    const newFilters = [...filters];
    if (updates.condition) {
      // Reset value when condition changes
      if (updates.condition === FilterCondition.BETWEEN) {
        updates.value = ['', ''];
      } else if (updates.condition === FilterCondition.IS_ONE_OF) {
        updates.value = [];
      } else {
        updates.value = '';
      }
    }
    newFilters[index] = { ...newFilters[index], ...updates };
    setFilters(newFilters);
    setIsDirty(true);
  };

  const updateFilterValue = (
    index: number,
    value: string | string[] | [string, string],
    subIndex?: number
  ) => {
    const newFilters = [...filters];

    // Guard that the filter at the specified index exists
    if (!newFilters[index]) {
      console.warn(`Filter at index ${index} does not exist`);
      return;
    }

    if (subIndex !== undefined) {
      // Verify that subIndex is a number and value is actually a string
      if (typeof subIndex !== 'number' || typeof value !== 'string') {
        console.error('subIndex must be a number and value must be a string when subIndex is provided');
        return;
      }

      // Get the current value and ensure it's treated as an array
      const currentValue = Array.isArray(newFilters[index].value)
        ? newFilters[index].value
        : ['', ''];

      // Shallow-copy the existing pair into a new [string, string] array
      // ensuring length 2 and defaulting missing entries to ''
      const newPair: [string, string] = [
        String(currentValue[0] || ''),
        String(currentValue[1] || '')
      ];

      // Update only the targeted index
      newPair[subIndex] = value;

      // Reassign the new tuple to newFilters[index].value
      newFilters[index].value = newPair;
    } else {
      newFilters[index].value = value;
    }
    setFilters(newFilters);
    setIsDirty(true);
  };

  const removeFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  // Naming element management
  const addNamingElement = () => {
    setNamingElements([...namingElements, { type: 'STRING_LITERAL', value: '' }]);
    setIsDirty(true);
  };

  const updateNamingElement = (index: number, updates: Partial<NamingElement>) => {
    const newElements = [...namingElements];
    newElements[index] = { ...newElements[index], ...updates };
    setNamingElements(newElements);
    setIsDirty(true);
  };

  const removeNamingElement = (index: number) => {
    setNamingElements(namingElements.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const moveNamingElement = (fromIndex: number, toIndex: number) => {
    const newElements = [...namingElements];
    const [removed] = newElements.splice(fromIndex, 1);
    newElements.splice(toIndex, 0, removed);
    setNamingElements(newElements);
    setIsDirty(true);
  };

  // Naming preview
  const getNamingPreview = () => {
    return generateNamingPreview({
      namingElements,
      namingSeparator,
      templateName,
      fileFormat,
      splitBy,
    });
  };

  // Save handler with comprehensive validation
  const handleSave = () => {
    // Clear previous errors
    setNameError('');
    setColumnsError('');
    setNamingError('');

    // Validate using utility function
    const validation = validateTemplate({
      templateName,
      existingTemplateNames,
      editingTemplateName: editingTemplate?.name ?? null,
      columns,
      namingElements,
      namingMaxLength,
    });

    if (!validation.isValid) {
      setNameError(validation.nameError);
      setColumnsError(validation.columnsError);
      setNamingError(validation.namingError);
      return;
    }

    const trimmedName = templateName.trim();
    const template: OutputTemplate = {
      report_type: reportType,
      columns: columns.map((c, i) => ({ ...c, order: i })),
      file_format: fileFormat,
    };

    if (filters.length > 0) {
      template.filters = filters;
    }

    if (splitBy) {
      template.split_by = splitBy;
      template.default_split_value = defaultSplitValue || 'unknown';
    }

    if (namingElements.length > 0) {
      template.naming_config = {
        elements: namingElements,
        separator: namingSeparator,
        max_length: namingMaxLength,
      };
    }

    onSave(trimmedName, template);
    onClose();
  };

  return {
    state: {
      templateName,
      reportType,
      columns,
      filters,
      fileFormat,
      splitBy,
      defaultSplitValue,
      namingElements,
      namingSeparator,
      namingMaxLength,
      nameError,
      columnsError,
      namingError,
      isDirty,
    },
    availableFields,
    stringFields,
    splitByFields,
    isLoadingColumns,

    setTemplateName: (name: string) => {
      setTemplateName(name);
      setNameError('');
      setIsDirty(true);
    },
    setNameError,
    setColumnsError,
    setNamingError,

    handleReportTypeChange,
    handleFileFormatChange,
    handleSplitByChange,
    handleDefaultSplitValueChange,
    handleNamingSeparatorChange,
    handleNamingMaxLengthChange,

    addColumn,
    updateColumn,
    removeColumn,
    moveColumn,

    addFilter,
    updateFilter,
    updateFilterValue,
    removeFilter,

    addNamingElement,
    updateNamingElement,
    removeNamingElement,
    moveNamingElement,

    handleSave,
    getNamingPreview,
  };
};

export default useTemplateEditorForm;
